import type { SupabaseClient } from "@supabase/supabase-js";

import { runOperatorWrite, type OperatorWriteOutcome } from "./operator-controls";

// Platform cockpit data layer (PRD-001P). Reads the founder account list (every tenant with
// slug + contract-lock state) from the superadmin-gated get_platform_account_controls RPC, and
// sets/clears a tenant freeze through upsert_account_contract_lock. Browser writes stay RPC-only.

/** The live-contract tenant — wears the LIVE CONTRACT badge in the picker. */
export const LIVE_CONTRACT_SLUG = "stryke-team-elite-protection";

export interface PlatformAccount {
  accountId: string;
  accountName: string;
  slug: string;
  contractLocked: boolean;
}

export interface ProvisionedPlatformAccount {
  accountId: string;
  accountName: string;
  firstAdminInviteId: string | null;
}

export interface RenamedPlatformAccount {
  accountId: string;
  accountName: string;
}

export type PlatformProvisionOutcome =
  | { ok: true; account: ProvisionedPlatformAccount }
  | { ok: false; reason: string };

export type PlatformRenameOutcome =
  | { ok: true; account: RenamedPlatformAccount }
  | { ok: false; reason: string };

export const SAFE_PLATFORM_PROVISION_FAILURE =
  "We could not confirm that the workspace was created. Refresh Accounts before trying again.";

export const SAFE_PLATFORM_RENAME_FAILURE =
  "We could not confirm that the workspace name was updated. Refresh Accounts before trying again.";

const KNOWN_PLATFORM_PROVISION_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to provision workspaces.",
  "Platform superadmin access is required to provision workspaces.",
  "Workspace name is required.",
  "Workspace name must be 120 characters or fewer.",
  "First admin email is not valid.",
  "Invite rate limit exceeded. Try again later.",
]);

const KNOWN_PLATFORM_RENAME_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to rename workspaces.",
  "Platform superadmin access is required to rename workspaces.",
  "Workspace id is required.",
  "Workspace name is required.",
  "Workspace name must be 120 characters or fewer.",
  "Workspace was not found.",
]);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/** Map one RPC row to a PlatformAccount, or null when the identity columns are missing. */
export function mapPlatformAccount(raw: Record<string, unknown>): PlatformAccount | null {
  const accountId = asString(raw.account_id);
  const accountName = asString(raw.account_name);
  const slug = asString(raw.account_slug);
  if (accountId === null || slug === null) {
    return null;
  }
  return {
    accountId,
    accountName: accountName ?? "Workspace",
    slug,
    contractLocked: raw.contract_locked === true,
  };
}

/** True when this tenant holds the live client contract (drives the badge). */
export function isLiveContractAccount(account: PlatformAccount): boolean {
  return account.slug === LIVE_CONTRACT_SLUG;
}

export async function fetchPlatformAccounts(client: SupabaseClient): Promise<PlatformAccount[]> {
  const { data, error } = await client.rpc("get_platform_account_controls");
  if (error !== null) {
    throw new Error("Could not load platform accounts.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapPlatformAccount(row as Record<string, unknown>))
    .filter((row): row is PlatformAccount => row !== null);
}

export async function upsertAccountContractLock(
  client: SupabaseClient,
  accountId: string,
  locked: boolean,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_contract_lock", {
    p_account_id: accountId,
    p_locked: locked,
  });
}

export function mapProvisionedPlatformAccount(data: unknown): ProvisionedPlatformAccount | null {
  const row = Array.isArray(data) ? data[0] : null;
  if (typeof row !== "object" || row === null || Array.isArray(row)) {
    return null;
  }

  const raw = row as Record<string, unknown>;
  const accountId = asString(raw.account_id);
  const accountName = asString(raw.account_name);
  const firstAdminInviteId = raw.first_admin_invite_id;
  if (
    accountId === null
    || !UUID_PATTERN.test(accountId)
    || accountName === null
    || (
      firstAdminInviteId !== null
      && (typeof firstAdminInviteId !== "string" || !UUID_PATTERN.test(firstAdminInviteId))
    )
  ) {
    return null;
  }

  return {
    accountId,
    accountName,
    firstAdminInviteId,
  };
}

export async function provisionPlatformAccount(
  client: SupabaseClient,
  name: string,
  firstAdminEmail: string | null,
): Promise<PlatformProvisionOutcome> {
  const trimmedEmail = firstAdminEmail?.trim() ?? "";
  try {
    const { data, error } = await client.rpc("provision_platform_account", {
      p_name: name.trim(),
      p_first_admin_email: trimmedEmail.length > 0 ? trimmedEmail : null,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_PLATFORM_PROVISION_FAILURES.has(message)
          ? message
          : SAFE_PLATFORM_PROVISION_FAILURE,
      };
    }

    const account = mapProvisionedPlatformAccount(data);
    if (
      account === null
      || (account.firstAdminInviteId !== null) !== (trimmedEmail.length > 0)
    ) {
      return { ok: false, reason: SAFE_PLATFORM_PROVISION_FAILURE };
    }
    return { ok: true, account };
  } catch {
    return { ok: false, reason: SAFE_PLATFORM_PROVISION_FAILURE };
  }
}

function mapRenamedPlatformAccount(
  data: unknown,
  expectedAccountId: string,
  expectedAccountName: string,
): RenamedPlatformAccount | null {
  if (!Array.isArray(data) || data.length !== 1) {
    return null;
  }
  const row = data[0];
  if (typeof row !== "object" || row === null || Array.isArray(row)) {
    return null;
  }

  const raw = row as Record<string, unknown>;
  const accountId = asString(raw.account_id);
  const accountName = asString(raw.account_name);
  if (accountId !== expectedAccountId || accountName !== expectedAccountName) {
    return null;
  }
  return { accountId, accountName };
}

/** Platform-only account-name change. The RPC has no slug field by contract. */
export async function platformRenameAccount(
  client: SupabaseClient,
  accountId: string,
  name: string,
): Promise<PlatformRenameOutcome> {
  const trimmedName = name.trim();
  try {
    const { data, error } = await client.rpc("platform_rename_account", {
      p_account_id: accountId,
      p_name: trimmedName,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_PLATFORM_RENAME_FAILURES.has(message)
          ? message
          : SAFE_PLATFORM_RENAME_FAILURE,
      };
    }

    const account = mapRenamedPlatformAccount(data, accountId, trimmedName);
    return account === null
      ? { ok: false, reason: SAFE_PLATFORM_RENAME_FAILURE }
      : { ok: true, account };
  } catch {
    return { ok: false, reason: SAFE_PLATFORM_RENAME_FAILURE };
  }
}
